/**
 * Soft Free-tier usage banner with Marketplace upgrade CTA when caps are near/hit.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '@/utils/api'

const MARKETPLACE_URL =
  'https://aws.amazon.com/marketplace/pp/prodview-hfarkmyxonwzu'

type QuotaPayload = {
  tier?: string
  limits?: {
    max_invocations_per_month?: number
    max_scheduled_tasks?: number
    max_email_alerts_per_month?: number
    max_seats?: number
  }
  usage?: {
    invocations_this_month?: number
  }
  headroom?: {
    invocations?: number
  }
}

export function PlanQuotaBanner() {
  const [quota, setQuota] = useState<QuotaPayload | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.get('/api/v1/marketplace/quota')
        if (!cancelled) setQuota(data)
      } catch {
        /* unauthenticated or unavailable — stay silent */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (!quota || (quota.tier || 'free').toLowerCase() !== 'free') return null

  const limit = Number(quota.limits?.max_invocations_per_month || 250)
  const used = Number(quota.usage?.invocations_this_month || 0)
  const headroom = Number(
    quota.headroom?.invocations ?? Math.max(0, limit - used),
  )
  const ratio = limit > 0 ? used / limit : 0
  if (ratio < 0.8 && headroom > 0) return null

  const atCap = headroom <= 0 || used >= limit

  return (
    <div
      className={
        atCap
          ? 'border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-950'
          : 'border-b border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-950'
      }
      role="status"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2">
        <p>
          {atCap
            ? `Free plan action limit reached (${used.toLocaleString()} / ${limit.toLocaleString()} this month).`
            : `Free plan: ${used.toLocaleString()} / ${limit.toLocaleString()} actions used this month.`}{' '}
          <span className="text-xs opacity-80">
            Caps: 1 seat · 5 tasks · 50 email alerts
          </span>
        </p>
        <a
          href={MARKETPLACE_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 rounded-md bg-beacon-orange px-3 py-1 text-xs font-semibold text-beacon-navy hover:bg-beacon-orange/90"
        >
          Upgrade on Marketplace
        </a>
        <Link
          to="/marketplace/register"
          className="text-xs font-medium underline underline-offset-2"
        >
          Already subscribed?
        </Link>
      </div>
    </div>
  )
}
